import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import SessionManager from '../../components/ui/SessionManager';
import PropertyFilters from './components/PropertyFilters';
import PropertyTable from './components/PropertyTable';
import PropertyDetails from './components/PropertyDetails';
import FilterToolbar from './components/FilterToolbar';
import TableSkeleton from '../../components/ui/TableSkeleton';
import { propertiesService } from '../../services/supabaseService';
import toast from 'react-hot-toast';

const PropertiesManagementScreen = () => {
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [filters, setFilters] = useState({
    landlord: 'all',
    type: 'all',
    status: 'all',
    area: 'all'
  });
  const [showDetails, setShowDetails] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const data = await propertiesService?.getAll();
      setProperties(data);
      setFilteredProperties(data);
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast?.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = [...properties];

    if (filters?.landlord !== 'all') {
      filtered = filtered?.filter(p => p?.landlord?.toLowerCase()?.includes(filters?.landlord?.toLowerCase()) || p?.landlordName?.toLowerCase()?.includes(filters?.landlord?.toLowerCase()));
    }

    if (filters?.type !== 'all') {
      filtered = filtered?.filter(p => p?.propertyType?.toLowerCase() === filters?.type || p?.type?.toLowerCase() === filters?.type);
    }

    if (filters?.status !== 'all') {
      filtered = filtered?.filter(p => p?.status?.toLowerCase()?.replace(' ', '-') === filters?.status || p?.status?.toLowerCase() === filters?.status);
    }

    setFilteredProperties(filtered);
  }, [filters, properties]);

  const handleFilterChange = (category, value) => {
    if (category === 'reset') {
      setFilters({
        landlord: 'all',
        type: 'all',
        status: 'all',
        area: 'all'
      });
    } else {
      setFilters(prev => ({
        ...prev,
        [category]: value
      }));
    }
  };

  const handlePropertySelect = (property) => {
    setSelectedProperty(property);
    setShowDetails(true);
  };

  const handleSearch = (query) => {
    if (!query?.trim()) {
      setFilteredProperties(properties);
      return;
    }

    const searchLower = query?.toLowerCase();
    let filtered = properties?.filter(p => 
      p?.propertyId?.toLowerCase()?.includes(searchLower) ||
      p?.address1?.toLowerCase()?.includes(searchLower) ||
      p?.postcode?.toLowerCase()?.includes(searchLower) ||
      p?.landlordName?.toLowerCase()?.includes(searchLower) ||
      (p?.tenantName && p?.tenantName?.toLowerCase()?.includes(searchLower))
    );

    setFilteredProperties(filtered);
  };

  const handleExport = () => {
    console.log('Exporting properties to CSV...');
    toast?.info('CSV export feature coming soon');
  };

  const handleImport = () => {
    console.log('Opening CSV import modal...');
    toast?.info('CSV import feature coming soon');
  };

  const propertyCounts = {
    total: properties?.length,
    occupied: properties?.filter(p => p?.status === 'occupied')?.length,
    vacant: properties?.filter(p => p?.status === 'vacant')?.length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <SessionManager />
        <main className="pt-[108px] px-4 md:px-6 lg:px-8 pb-8">
          <div className="max-w-[1920px] mx-auto">
            <div className="mb-6">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2 font-heading">
                Properties Management
              </h1>
              <p className="text-sm md:text-base text-muted-foreground font-caption">
                Manage your complete property portfolio with advanced filtering and bulk operations
              </p>
            </div>

            <FilterToolbar
              onSearch={() => {}}
              onExport={() => {}}
              onImport={() => {}}
              selectedCount={0}
              onDateFilter={() => {}}
            />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
              <div className="lg:col-span-3">
                <div className="bg-surface rounded-lg border border-border p-4">
                  <div className="space-y-3">
                    <div className="h-8 bg-muted rounded animate-shimmer" />
                    <div className="h-6 bg-muted/70 rounded animate-shimmer" />
                    <div className="h-6 bg-muted/70 rounded animate-shimmer" />
                    <div className="h-6 bg-muted/70 rounded animate-shimmer" />
                  </div>
                </div>
              </div>

              <div className="lg:col-span-9">
                <div className="bg-surface rounded-lg border border-border p-6">
                  <TableSkeleton rows={8} columns={6} />
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <SessionManager />
      <main className="pt-[108px] px-4 md:px-6 lg:px-8 pb-8">
        <div className="max-w-[1920px] mx-auto">
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2 font-heading">
              Properties Management
            </h1>
            <p className="text-sm md:text-base text-muted-foreground font-caption">
              Manage your complete property portfolio with advanced filtering and bulk operations
            </p>
          </div>

          <FilterToolbar
            onSearch={handleSearch}
            onExport={handleExport}
            onImport={handleImport}
            selectedCount={selectedRows?.length}
            onDateFilter={() => {}}
          />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
            <div className="lg:col-span-3">
              <div className="bg-surface rounded-lg border border-border h-[calc(100vh-280px)] lg:h-[calc(100vh-240px)]">
                <PropertyFilters
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  propertyCounts={propertyCounts}
                />
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="h-[calc(100vh-280px)] lg:h-[calc(100vh-240px)]">
                <PropertyTable
                  properties={filteredProperties}
                  onPropertySelect={handlePropertySelect}
                  selectedProperty={selectedProperty}
                  onBulkSelect={setSelectedRows}
                  selectedRows={selectedRows}
                />
              </div>
            </div>

            <div className={`lg:col-span-3 ${showDetails ? 'block' : 'hidden lg:block'}`}>
              <div className="h-[calc(100vh-280px)] lg:h-[calc(100vh-240px)]">
                <PropertyDetails
                  property={selectedProperty}
                  onClose={() => setShowDetails(false)}
                />
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PropertiesManagementScreen;